<template>
  <div class="background-image-picker">
    <NebulaButton
      :disabled="isAssetSlide"
      icon-left="search"
      size="s"
      :text="$t('Search DE')"
      type="flat"
      @click="openSearchAssetsModal"
    />
    <NebulaButton
      :disabled="isAssetSlide"
      icon-left="upload"
      size="s"
      :text="$t('Upload from My Device')"
      type="flat"
      @click="openMyContentUploadModal"
    />
    <div class="background-image-picker__selected-background">
      <div
        class="background-image-picker__preview"
        :class="{
          'background-image-picker__preview--no-background': !selectedBackgroundImage,
        }"
        :style="{
          backgroundImage: (isLoading || !selectedBackgroundImage)
            ? undefined
            : `url(${validThumbnailUrl})`,
        }"
      >
        <LoadingIndicator
          v-if="isLoading"
          class="background-image-picker__loading-indicator"
          absolute
          transparent
        />
      </div>
      <div class="background-image-picker__preview-label">
        <template v-if="asset && asset.title">
          {{ asset.title }}
        </template>
        <template v-else>
          {{ draftType === 'board' ? $t('No Page Background') : $t('No Slide Background') }}
        </template>
      </div>
      <NebulaButton
        v-if="selectedBackgroundImage"
        class="background-image-picker__clear-button"
        :aria-label="draftType === 'board'
          ? $t('Clear Page Background')
          : $t('Clear Slide Background')"
        icon-left="trash"
        size="l"
        type="flat"
        @click="clearBackgroundImage"
      />
    </div>
  </div>
</template>

<script>
import { NebulaButton } from '@discoveryedu/nebula-components';
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import assetMixin from '@/mixins/asset';
import thumbnailLoader from '@/mixins/thumbnailLoader';
import * as types from '@/lib/constants/store';
import {
  useAssetStore,
  useEditorStore,
  useModalStore,
} from '@/stores';

export default {
  name: 'BackgroundImagePicker',
  components: {
    LoadingIndicator,
    NebulaButton,
  },
  mixins: [
    assetMixin, // handles pulling thumbnails urls from an asset
    thumbnailLoader, // handles finding valid thumbnails
  ],
  data() {
    return {
      ignoreResize: true, // used by the asset mixin
      useLargeImageUrls: true, // used by the thumbnail loader
    };
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'draftType',
    ]),
    ...mapState(useAssetStore, [
      'assets',
    ]),
    asset() {
      return this.assets[this.selectedBackgroundImage];
    },
    isAssetSlide() {
      return !!get(this.currentPage.options, 'asset_id');
    },
    isLoading() {
      if (!this.selectedBackgroundImage) return false;

      return !this.asset
        || this.thumbnailIsLoading
        || !this.validThumbnailUrl;
    },
    pageTheme() {
      return get(this, 'currentPage.options.theme', {});
    },
    selectedBackgroundImage() {
      return this.pageTheme.background_image;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_PAGE_OPTIONS,
    ]),
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    clearBackgroundImage() {
      this[types.UPDATE_PAGE_OPTIONS]({
        options: {
          ...this.currentPage.options,
          theme: {
            ...this.pageTheme,
            background_image: undefined,
          },
        },
      });
    },
    openMyContentUploadModal() {
      this[types.OPEN_MODAL]({
        type: 'EditorialUploadInfo',
        allowMultiple: false,
        onConfirm: (assets) => {
          // Selected assets are returned as an array, but we only expect one
          const assetId = assets[0]?.id;

          // Keep the rest of the page's theme the same but update the background image
          this[types.UPDATE_PAGE_OPTIONS]({
            options: {
              ...this.currentPage.options,
              theme: {
                ...this.pageTheme,
                background_image: assetId,
              },
            },
          });
        },
      });
    },
    openSearchAssetsModal() {
      this[types.OPEN_MODAL]({
        type: 'SearchAssets',
        onConfirm: (assets) => {
          // Selected assets are returned as an array, but we only expect one
          const assetId = assets[0]?.id;

          // Keep the rest of the page's theme the same but update the background image
          this[types.UPDATE_PAGE_OPTIONS]({
            options: {
              ...this.currentPage.options,
              theme: {
                ...this.pageTheme,
                background_image: assetId,
              },
            },
          });
        },
        singleSelect: true,
      });
    },
  },
};
</script>

<style lang="stylus">
.background-image-picker {
  padding: $nebula-space-2x;

  &__selected-background {
    align-items: center;
    background-color: $nebula-color-platform-neutral-300;
    border-radius: $nebula-border-radius-badge-default;
    display: flex;
    flex-shrink: 0;
    font-weight: bold;
    margin: $nebula-space-2x 0 0;
  }

  &__preview {
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    background-color: #414858; // default background color
    flex-shrink: 0;
    height: 42px;
    margin: $nebula-space-1x;
    position: relative;
    width: 56px;

    &.background-image-picker__preview--no-background {
      background: linear-gradient(143deg, $nebula-color-white 48.5%, $nebula-color-error 49%,
        $nebula-color-error 51%, $nebula-color-white 51.5%);
    }
  }

  &__preview-label {
    flex-grow: 1;
  }

  &__clear-button.nebula-button {
    align-self: stretch;
    background-color: $nebula-color-platform-neutral-300;
    border-radius: 0 $nebula-border-radius-badge-default $nebula-border-radius-badge-default 0;
    margin: 0;

    svg {
      fill: inherit;
    }

    &:hover, &:focus {
      background-color: $nebula-color-platform-neutral-400;
    }

    &:active {
      background-color: $nebula-color-platform-neutral-500;

      svg {
        fill: inherit;
      }
    }
  }

  &__loading-indicator {
    .comet-spinner__icon {
      height: $nebula-space-3x;
      width: $nebula-space-3x;
    }
  }
}
</style>
